import { FREQUENCY_LABEL } from "pages/patient/PatientMedicationPage/components/DrugsTable/constants";

import { DataLoadError } from "components/ui/DataLoadError";
import { InfoTooltip } from "components/ui/InfoTooltip";
import {
  MECC_INTERVENTION_INFO,
  PHYSICAL_ACTIVITY_Q1_OPTIONS,
  SMOKING_STATUS_OPTIONS,
  VACCINE_OPTIONS,
} from "features/cdm/constants/formOptions";
import { usePaginatedLongTermMedications } from "features/medications/hooks/usePaginatedLongTermMedications";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";

import style from "../CarePlanForm.module.css";
import { CARE_PLAN_INFO } from "../constants";

const labelOf = (options, value) =>
  options.find((option) => option.value === value)?.label ?? value;

const present = (value) =>
  value !== undefined && value !== null && value !== "";

const isNotAvailable = (value) =>
  ["na", "not available"].includes(String(value).trim().toLowerCase());

// Append a unit only when there is a real numeric value — "Not available"
// must not carry a unit (e.g. avoid "Not Available mmol/L").
const withUnit = (value, unit) =>
  isNotAvailable(value) ? "Not available" : `${value} ${unit}`;

// Body-weight MECC band derived from BMI (thresholds per HSE care plan screen).
const bodyWeightBand = (bmiValue) => {
  const bmi = parseFloat(bmiValue);
  if (!bmi) return null;
  if (bmi < 18.5) return "Underweight (BMI < 18.5)";
  if (bmi < 25) return "Low Risk (BMI ≥ 18.5 and < 25)";
  if (bmi < 30) return "Increased Risk (BMI ≥ 25 and < 30)";
  return "High Risk (BMI ≥ 30)";
};

const SummaryRow = ({ label, value }) =>
  present(value) ? (
    <div className={style.summaryRow}>
      <span className={style.summaryLabel}>{label}</span>
      <span className={style.summaryValue}>{value}</span>
    </div>
  ) : null;

// Read-only sections auto-populated from the current review draft + patient
// record, mirroring the HSE "Care Plan" GP screen. Nothing here is captured —
// it is a summary of what auto-populates onto the handout.
export const CarePlanAutoBlocks = ({ data }) => {
  const patientId = useCurrentPatientId();
  // Mirror the "Medication Review" panel above: the patient's live long-term
  // GP prescriptions, not the review-scoped care-plan medications (which are
  // empty until a prior review exists).
  const {
    data: longTermMedications,
    isError: isMedicationsError,
    refetch: refetchMedications,
  } = usePaginatedLongTermMedications(patientId, 1, "", 9999);
  const medications = longTermMedications?.data ?? [];
  const review = data ?? {};

  const examRows = [
    { label: "Weight", value: present(review.weight) ? withUnit(review.weight, "kg") : null },
    { label: "BMI", value: review.bmi },
    {
      label: "BP",
      value:
        present(review.bp1) && present(review.bp2)
          ? `${review.bp1} / ${review.bp2}`
          : null,
    },
    {
      label: "Total Cholesterol",
      value: present(review.totalCholesterol) ? withUnit(review.totalCholesterol, "mmol/L") : null,
    },
    {
      label: "LDL Cholesterol",
      value: present(review.ldlCholesterol) ? withUnit(review.ldlCholesterol, "mmol/L") : null,
    },
    {
      label: "HDL Cholesterol",
      value: present(review.hdlCholesterol) ? withUnit(review.hdlCholesterol, "mmol/L") : null,
    },
    {
      label: "Triglycerides",
      value: present(review.triglycerides) ? withUnit(review.triglycerides, "mmol/L") : null,
    },
    {
      label: "HbA1c",
      value: present(review.hba1c) ? withUnit(review.hba1c, "mmol/mol") : null,
    },
    // eGFR is matrix-gated (captured only for conditions whose investigations
    // matrix flags it), so present() naturally limits it to where it was taken.
    {
      label: "eGFR",
      value: present(review.egfr) ? withUnit(review.egfr, "mL/min") : null,
    },
  ].filter((row) => present(row.value));

  const meccRows = [
    {
      label: "Smoking",
      value: present(review.smokingStatus)
        ? labelOf(SMOKING_STATUS_OPTIONS, review.smokingStatus)
        : null,
    },
    {
      label: "Alcohol",
      value: present(review.alcoholRiskScore)
        ? `Score: ${review.alcoholRiskScore}`
        : null,
    },
    { label: "Body Weight", value: bodyWeightBand(review.bmi) },
    {
      label: "Physical Activity",
      value: present(review.physicalActivityQ1)
        ? labelOf(PHYSICAL_ACTIVITY_Q1_OPTIONS, review.physicalActivityQ1)
        : null,
    },
  ].filter((row) => present(row.value));

  const vaccineRows = [
    { label: "Annual Influenza Vaccine", code: review.influenzaVaccine },
    { label: "Pneumococcal Vaccine", code: review.pneumococcalVaccine },
    { label: "Covid 19 Vaccine", code: review.covidVaccine },
  ]
    .filter((row) => present(row.code))
    .map((row) => ({ label: row.label, value: labelOf(VACCINE_OPTIONS, row.code) }));

  return (
    <>
      <section className={style.section}>
        <h3>Medications</h3>
        {medications.length ? (
          <ul className={style.autoList}>
            {medications.map((medication, index) => (
              <li key={index}>
                {[
                  medication.tradeName,
                  medication.dosage,
                  medication.frequency
                    ? FREQUENCY_LABEL(medication.frequency)
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </li>
            ))}
          </ul>
        ) : isMedicationsError ? (
          // This block is printed onto the patient's care plan, so an empty
          // medication list must not stand in for a failed fetch (KIN-1616).
          <DataLoadError
            label="this patient's medications"
            text="This care plan would go out with the medication section empty, which is not a statement that they are on none."
            onRetry={refetchMedications}
          />
        ) : (
          <p className={style.autoEmpty}>No medications on record.</p>
        )}
      </section>

      {examRows.length > 0 && (
        <section className={style.section}>
          <h3 className={style.headingWithInfo}>
            Results of my physical examination and blood tests that were
            discussed today
            <InfoTooltip text={CARE_PLAN_INFO.examResults} />
          </h3>
          <div className={style.summaryBlock}>
            {examRows.map((row) => (
              <SummaryRow key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </section>
      )}

      {meccRows.length > 0 && (
        <section className={style.section}>
          <h3 className={style.headingWithInfo}>
            MECC Intervention
            <InfoTooltip text={MECC_INTERVENTION_INFO} />
          </h3>
          <div className={style.summaryBlock}>
            {meccRows.map((row) => (
              <SummaryRow key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </section>
      )}

      {vaccineRows.length > 0 && (
        <section className={style.section}>
          <h3>Vaccinations</h3>
          <div className={style.summaryBlock}>
            {vaccineRows.map((row) => (
              <SummaryRow key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        </section>
      )}
    </>
  );
};
